import { useReducer } from 'react';

const initialState = {
  comments: [],
  isLoading: false,
  loadingMessage: '',
  isError: false
};

const ACTION_TYPES = {
  // add comment to patient
  ADD_COMMENT_TO_PATIENT_REQUEST: 'ADD_COMMENT_TO_PATIENT_REQUEST',
  ADD_COMMENT_TO_PATIENT_SUCCESS: 'ADD_COMMENT_TO_PATIENT_SUCCESS',
  ADD_COMMENT_TO_PATIENT_FAILURE: 'ADD_COMMENT_TO_PATIENT_FAILURE',

  // add comment to encounter
  ADD_COMMENT_TO_ENCOUNTER_REQUEST: 'ADD_COMMENT_TO_ENCOUNTER_REQUEST',
  ADD_COMMENT_TO_ENCOUNTER_SUCCESS: 'ADD_COMMENT_TO_ENCOUNTER_SUCCESS',
  ADD_COMMENT_TO_ENCOUNTER_FAILURE: 'ADD_COMMENT_TO_ENCOUNTER_FAILURE',

  DELETE_COMMENT_REQUEST: 'DELETE_COMMENT_REQUEST',
  DELETE_COMMENT_SUCCESS: 'DELETE_COMMENT_SUCCESS',
  DELETE_COMMENT_FAILURE: 'DELETE_COMMENT_FAILURE',

  SAVE_COMMENT_REQUEST: 'SAVE_COMMENT_REQUEST',
  SAVE_COMMENT_SUCCESS: 'SAVE_COMMENT_SUCCESS',
  SAVE_COMMENT_FAILURE: 'SAVE_COMMENT_FAILURE',

  SET_COMMENT_INACTIVE_REQUEST: 'SET_COMMENT_INACTIVE_REQUEST',
  SET_COMMENT_INACTIVE_SUCCESS: 'SET_COMMENT_INACTIVE_SUCCESS',
  SET_COMMENT_INACTIVE_FAILURE: 'SET_COMMENT_INACTIVE_FAILURE',

  HYDRATE: `HYDRATE`
};

const ACTIONS = {
  // add comment to patient
  addCommentToPatientRequest: () => ({
    type: ACTION_TYPES.ADD_COMMENT_TO_PATIENT_REQUEST
  }),
  addCommentToPatientSuccess: createdComment => ({
    type: ACTION_TYPES.ADD_COMMENT_TO_PATIENT_SUCCESS,
    payload: {
      createdComment
    }
  }),
  addCommentToPatientFailure: error => ({
    type: ACTION_TYPES.ADD_COMMENT_TO_PATIENT_FAILURE,
    error
  }),

  // add comment to encounter
  addCommentToEncounterRequest: () => ({
    type: ACTION_TYPES.ADD_COMMENT_TO_ENCOUNTER_REQUEST
  }),
  addCommentToEncounterSuccess: (
    currentEncounterId,
    createdComment,
    targetEncounterId
  ) => ({
    type: ACTION_TYPES.ADD_COMMENT_TO_ENCOUNTER_SUCCESS,
    payload: {
      currentEncounterId,
      createdComment,
      targetEncounterId
    }
  }),
  addCommentToEncounterFailure: error => ({
    type: ACTION_TYPES.ADD_COMMENT_TO_ENCOUNTER_FAILURE,
    error
  }),
  saveCommentRequest: () => ({
    type: ACTION_TYPES.SAVE_COMMENT_REQUEST
  }),
  saveCommentSuccess: comment => ({
    type: ACTION_TYPES.SAVE_COMMENT_SUCCESS,
    payload: comment
  }),
  saveCommentFailure: error => ({
    type: ACTION_TYPES.SAVE_COMMENT_FAILURE,
    payload: error
  }),
  deleteCommentReqeust: () => ({
    type: ACTION_TYPES.DELETE_COMMENT_REQUEST
  }),
  deleteCommentSuccess: commentId => ({
    type: ACTION_TYPES.DELETE_COMMENT_SUCCESS,
    payload: { commentId }
  }),
  deleteCommentFailure: error => ({
    type: ACTION_TYPES.DELETE_COMMENT_FAILURE,
    payload: error
  }),
  setCommentInactiveRequest: () => ({
    type: ACTION_TYPES.SET_COMMENT_INACTIVE_REQUEST
  }),
  setCommentInactiveSuccess: comment => ({
    type: ACTION_TYPES.SET_COMMENT_INACTIVE_SUCCESS,
    payload: comment
  }),
  setCommentInactiveFailure: error => ({
    type: ACTION_TYPES.SET_COMMENT_INACTIVE_FAILURE,
    payload: error
  }),
  hydrate: comments => ({
    type: ACTION_TYPES.HYDRATE,
    payload: {
      comments
    }
  })
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.ADD_COMMENT_TO_PATIENT_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Adding comment to patient'
      };
    }

    case ACTION_TYPES.ADD_COMMENT_TO_PATIENT_SUCCESS: {
      const { payload } = action;

      return {
        ...state,
        isLoading: false,
        loadingMessage: '',
        comments: [payload.createdComment, ...state.comments]
      };
    }

    case ACTION_TYPES.ADD_COMMENT_TO_PATIENT_FAILURE: {
      return { ...state, isLoading: false, loadingMessage: '' };
    }

    case ACTION_TYPES.HYDRATE: {
      const { payload: { comments } } = action;
      return { ...state, comments };
    }

    case ACTION_TYPES.ADD_COMMENT_TO_ENCOUNTER_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Adding comment to encounter'
      };
    }

    case ACTION_TYPES.ADD_COMMENT_TO_ENCOUNTER_SUCCESS: {
      const { payload } = action;

      if (
        payload.createdComment.commentSourceId.toString()
          !== payload.currentEncounterId
      ) {
        return {
          ...state,
          isLoading: false,
          loadingMessage: '',
          comments: [...state.comments]
        };
      } else {
        return {
          ...state,
          isLoading: false,
          loadingMessage: '',
          comments: [payload.createdComment, ...state.comments]
        };
      }
    }

    case ACTION_TYPES.ADD_COMMENT_TO_ENCOUNTER_FAILURE: {
      return { ...state, isLoading: false, loadingMessage: '' };
    }

    case ACTION_TYPES.SAVE_COMMENT_SUCCESS:
    case ACTION_TYPES.SET_COMMENT_INACTIVE_SUCCESS: {
      const updatedComment = action.payload;

      let tempComments = state.comments.slice();
      let newComments = tempComments.filter(item => item.commentId !== updatedComment.commentId);
      newComments.push(updatedComment);
      return {
        ...state,
        isLoading: false,
        loadingMessage: '',
        comments: newComments
      };
    }

    case ACTION_TYPES.SET_COMMENT_INACTIVE_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Setting comment to inactive'
      };
    }

    case ACTION_TYPES.DELETE_COMMENT_FAILURE:
    case ACTION_TYPES.SAVE_COMMENT_FAILURE:
    case ACTION_TYPES.SET_COMMENT_INACTIVE_FAILURE: {
      return { ...state, isLoading: false, loadingMessage: '' };
    }

    case ACTION_TYPES.SAVE_COMMENT_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Saving comment'
      };
    }

    case ACTION_TYPES.DELETE_COMMENT_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Deleting comment'
      };
    }

    case ACTION_TYPES.DELETE_COMMENT_SUCCESS: {
      const { payload: { commentId } } = action;
      let tempComments = state.comments.slice();
      let newComments = tempComments.filter(item => item.commentId !== commentId);
      return {
        ...state,
        isLoading: false,
        loadingMessage: '',
        comments: newComments
      };
    }

    default: {
      throw Error('useCommentSection reducer should never hit default')
    }
  }
};

const useCommentSection = (service, currentEncounterId) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const addPatientComment = (patientId, commentText) => {
    dispatch(ACTIONS.addCommentToPatientRequest());

    return service.addCommentToPatient(patientId, commentText)
      .then(createdComment => {
        dispatch(ACTIONS.addCommentToPatientSuccess(createdComment));
        return Promise.resolve(null);
      })
      .catch(e => dispatch(ACTIONS.addCommentToPatientFailure(e.toString())));
  };

  const addEncounterComment = (encounterId, commentText) => {
    dispatch(ACTIONS.addCommentToEncounterRequest());

    return service.addCommentToEncounter(encounterId, commentText)
      .then(createdComment => {
        dispatch(
          ACTIONS.addCommentToEncounterSuccess(
            currentEncounterId,
            createdComment,
            encounterId
          )
        );
        return Promise.resolve(null);
      })
      .catch(e => dispatch(ACTIONS.addCommentToEncounterFailure(e.toString())));
  }

  const saveComment = comment => {
    dispatch(ACTIONS.saveCommentRequest());

    return service.saveComment(comment)
      .then(returnComment => {
        dispatch(
          ACTIONS.saveCommentSuccess(returnComment)
        );
        return Promise.resolve(null);
      })
      .catch(e => dispatch(ACTIONS.saveCommentFailure(e.toString())));
  }

  const deleteComment = commentId => {
    dispatch(ACTIONS.deleteCommentReqeust());

    return service.deleteComment(commentId)
      .then(result => {
        dispatch(ACTIONS.deleteCommentSuccess(commentId));
      })
      .catch(e => dispatch(ACTIONS.deleteCommentFailure(e.toString())));
  }

  const setCommentInactive = comment => {
    dispatch(ACTIONS.setCommentInactiveRequest());

    return service.setCommentInActive(comment)
      .then(returnComment => {
        dispatch(
          ACTIONS.setCommentInactiveSuccess(returnComment)
        );
        return Promise.resolve(null);
      })
      .catch(e => dispatch(ACTIONS.setCommentInactiveFailure(e.toString())));
  }

  const hydrate = comments => dispatch(ACTIONS.hydrate(comments));

  const boundActions = {
    addPatientComment,
    addEncounterComment,
    saveComment,
    deleteComment,
    setCommentInactive,
    hydrate
  };

  return [state, boundActions];
};

export default useCommentSection;
